/**
 * Scheduler Store - Zustand State Management
 * 
 * Manages schedulers (recurring payments) state.
 * Premium feature only.
 * 
 * @validates Requirements 9.1-9.8 - Scheduler management
 */

import { create } from 'zustand';
import { useAccountStore } from './accountStore';
import { 
  Scheduler, 
  CreateSchedulerRequest, 
  UpdateSchedulerRequest,
  OperationToSchedulerRequest,
  Periodicity 
} from '@budgira/shared';
import { schedulerService } from '../services/scheduler.service';

interface SchedulerState {
  schedulers: Scheduler[];
  isLoading: boolean;
  error: string | null;
  statusFilter: 'all' | 'active' | 'terminated';
  page: number;
  pageSize: number;
  
  // Actions
  fetchSchedulers: (accountId: string) => Promise<void>;
  getScheduler: (id: string) => Promise<Scheduler | null>;
  createScheduler: (accountId: string, data: CreateSchedulerRequest) => Promise<Scheduler | null>;
  createTransferScheduler: (data: {
    sourceAccountId: string;
    destinationAccountId: string;
    startDate: string;
    endDate?: string;
    amount: number;
    periodicity: string;
    debitCategoryId: string;
    creditCategoryId: string;
    reference?: string;
    comment?: string;
    autoPoint?: boolean;
    businessDayAdjustment?: string;
  }) => Promise<{ sourceScheduler: Scheduler; destinationScheduler: Scheduler } | null>;
  updateScheduler: (id: string, data: UpdateSchedulerRequest) => Promise<Scheduler | null>;
  deleteScheduler: (id: string) => Promise<boolean>;
  terminateScheduler: (id: string) => Promise<Scheduler | null>;
  createFromOperation: (operationId: string, data: OperationToSchedulerRequest) => Promise<Scheduler | null>;
  generateOperations: (accountId: string, untilDate: string) => Promise<number>;
  setStatusFilter: (status: 'all' | 'active' | 'terminated') => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  clearError: () => void;
}

const DEFAULT_PAGE_SIZE = 25;

export const useSchedulerStore = create<SchedulerState>((set, get) => ({
  schedulers: [],
  isLoading: false,
  error: null,
  statusFilter: 'all',
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,

  fetchSchedulers: async (accountId: string): Promise<void> => {
    const { statusFilter } = get();
    // Clear schedulers immediately to show loading state for new account
    set({ isLoading: true, error: null, schedulers: [] });
    
    try {
      const response = await schedulerService.list(accountId, statusFilter);
      if (response.success && response.data) {
        set({
          schedulers: response.data,
          isLoading: false,
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch schedulers', isLoading: false });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
    }
  },

  getScheduler: async (id: string): Promise<Scheduler | null> => {
    try {
      const response = await schedulerService.getById(id);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch {
      return null;
    }
  },

  createScheduler: async (accountId: string, data: CreateSchedulerRequest): Promise<Scheduler | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await schedulerService.create(accountId, data);
      if (response.success && response.data) {
        const newScheduler = response.data;
        set((state) => ({
          schedulers: [...state.schedulers, newScheduler],
          isLoading: false,
        }));
        return newScheduler;
      } else {
        set({ error: response.error?.message || 'Failed to create scheduler', isLoading: false });
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  createTransferScheduler: async (data: {
    sourceAccountId: string;
    destinationAccountId: string;
    startDate: string;
    endDate?: string;
    amount: number;
    periodicity: string;
    debitCategoryId: string;
    creditCategoryId: string;
    reference?: string;
    comment?: string;
    autoPoint?: boolean;
    businessDayAdjustment?: string;
  }): Promise<{ sourceScheduler: Scheduler; destinationScheduler: Scheduler } | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await schedulerService.createTransferScheduler(data);
      if (response.success && response.data) {
        // Refresh the list for the source account
        await get().fetchSchedulers(data.sourceAccountId);
        set({ isLoading: false });
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to create transfer scheduler', isLoading: false });
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updateScheduler: async (id: string, data: UpdateSchedulerRequest): Promise<Scheduler | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await schedulerService.update(id, data);
      if (response.success && response.data) {
        const updatedScheduler = response.data;
        set((state) => ({
          schedulers: state.schedulers.map((s) => (s.id === id ? updatedScheduler : s)),
          isLoading: false,
        }));
        return updatedScheduler;
      } else {
        set({ error: response.error?.message || 'Failed to update scheduler', isLoading: false });
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  deleteScheduler: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const response = await schedulerService.delete(id);
      if (response.success) {
        set((state) => ({
          schedulers: state.schedulers.filter((s) => s.id !== id),
          isLoading: false,
        }));
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to delete scheduler', isLoading: false });
        return false;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return false;
    }
  },

  terminateScheduler: async (id: string): Promise<Scheduler | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await schedulerService.terminate(id);
      if (response.success && response.data) {
        const terminatedScheduler = response.data;
        set((state) => ({
          schedulers: state.schedulers.map((s) => (s.id === id ? terminatedScheduler : s)),
          isLoading: false,
        }));
        return terminatedScheduler;
      } else {
        set({ error: response.error?.message || 'Failed to terminate scheduler', isLoading: false });
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  createFromOperation: async (operationId: string, data: OperationToSchedulerRequest): Promise<Scheduler | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await schedulerService.createFromOperation(operationId, data);
      if (response.success && response.data) {
        const newScheduler = response.data;
        set((state) => ({
          schedulers: [...state.schedulers, newScheduler],
          isLoading: false,
        }));
        return newScheduler;
      } else {
        set({ error: response.error?.message || 'Failed to create scheduler from operation', isLoading: false });
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  generateOperations: async (accountId: string, untilDate: string): Promise<number> => {
    set({ isLoading: true, error: null });
    try {
      const response = await schedulerService.generateOperations(accountId, untilDate);
      if (response.success && response.data) {
        set({ isLoading: false });
        // Refresh balance for the account after generating operations
        useAccountStore.getState().fetchBalance(accountId);
        return response.data.generatedCount;
      } else {
        set({ error: response.error?.message || 'Failed to generate operations', isLoading: false });
        return 0;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return 0;
    }
  },

  setStatusFilter: (status: 'all' | 'active' | 'terminated'): void => {
    set({ statusFilter: status, page: 1 });
  },

  setPage: (page: number): void => {
    set({ page });
  },

  setPageSize: (pageSize: number): void => {
    set({ pageSize, page: 1 });
  },

  clearError: () => set({ error: null }),
}));

/**
 * Helper to get periodicity label
 */
export const getPeriodicityLabel = (periodicity: Periodicity, t: (key: string) => string): string => {
  const labels: Record<Periodicity, string> = {
    'daily': t('schedulers.periodicity.daily'),
    'weekly': t('schedulers.periodicity.weekly'),
    'bi-weekly': t('schedulers.periodicity.biWeekly'),
    'monthly': t('schedulers.periodicity.monthly'),
    'quarterly': t('schedulers.periodicity.quarterly'),
    'semi-annually': t('schedulers.periodicity.semiAnnually'),
    'annually': t('schedulers.periodicity.annually'),
  };
  return labels[periodicity] || periodicity;
};

export default useSchedulerStore;
